import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useDomains, useCreateDomain, useDeleteDomain, useRefreshDomain, useUpdateDomain } from '@/hooks/useDomains'
import { useServices } from '@/hooks/useServices'
import { api } from '@/api/client'
import type { DomainRecord, Service } from '@/api/types'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  FormRow,
  Input,
  Select,
  Table,
} from '@/admin-design/_common'
import type { BadgeVariant } from '@/admin-design/_common'

// pending=warning / issued=success / expired=destructive / unknown=muted
const certVariant: Record<string, BadgeVariant> = {
  pending: 'warning',
  issued: 'success',
  expired: 'destructive',
}

const certDot: Record<string, string> = {
  issued: 'bg-success',
  pending: 'bg-warning',
  expired: 'bg-destructive',
}

// RedirectCell: shows the redirect target for a domain and lets the user
// edit it inline. Saving sends a PATCH which the server turns into a
// re-rendered nginx conf (301 template instead of proxy) + reload.
function RedirectCell({ domain, onSave }: { domain: DomainRecord; onSave: (value: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(domain.redirect_to ?? '')
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => { setValue(domain.redirect_to ?? ''); setEditing(true) }}
        className="text-left text-xs text-muted-foreground hover:text-foreground"
      >
        {domain.redirect_to ? (
          <span>→ {domain.redirect_to}</span>
        ) : (
          <span className="text-muted-foreground">— set redirect</span>
        )}
      </button>
    )
  }
  const commit = () => {
    const v = value.trim()
    if (v !== (domain.redirect_to ?? '')) {
      onSave(v)
    }
    setEditing(false)
  }
  return (
    <div className="flex items-center gap-1">
      <Input
        autoFocus
        className="h-auto px-1.5 py-0.5 text-xs"
        placeholder="www.example.com"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
          else if (e.key === 'Escape') setEditing(false)
        }}
        onBlur={commit}
      />
    </div>
  )
}

// genAuthToken returns a 64-char hex token (32 random bytes). Hex satisfies the
// server's [A-Za-z0-9._-] auth_token validation.
function genAuthToken(): string {
  const b = new Uint8Array(32)
  crypto.getRandomValues(b)
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
}

// AuthCell: shows whether a domain requires an Authorization: Bearer token and
// lets the operator set/clear it inline. Saving sends a PATCH; the server
// re-renders the nginx conf with a token gate in front of the upstream (or
// removes it when cleared). Use for externally-exposed APIs with no auth of
// their own (e.g. ollama on the GPU node).
function AuthCell({ domain, onSave }: { domain: DomainRecord; onSave: (value: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(domain.auth_token ?? '')
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => { setValue(domain.auth_token ?? ''); setEditing(true) }}
        className="text-left text-xs hover:text-foreground"
      >
        {domain.auth_token ? (
          <span className="text-foreground">🔒 token set</span>
        ) : (
          <span className="text-muted-foreground">— public</span>
        )}
      </button>
    )
  }
  const commit = () => {
    const v = value.trim()
    if (v !== (domain.auth_token ?? '')) {
      onSave(v)
    }
    setEditing(false)
  }
  return (
    <div className="flex items-center gap-1">
      <Input
        autoFocus
        className="h-auto px-1.5 py-0.5 text-xs font-mono"
        placeholder="Bearer token (empty = public)"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
          else if (e.key === 'Escape') setEditing(false)
        }}
        onBlur={commit}
      />
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="h-auto px-1.5 py-0.5 text-[11px]"
        // preventDefault keeps the input from blur-committing the old value
        // before the generated token lands.
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          const t = genAuthToken()
          setValue(t)
          onSave(t)
          setEditing(false)
        }}
      >
        Gen
      </Button>
    </div>
  )
}

export default function Domains() {
  const { data: domains, isLoading } = useDomains()
  const [showModal, setShowModal] = useState(false)
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [configId, setConfigId] = useState<number | null>(null)
  const deleteDomain = useDeleteDomain()
  const refreshDomain = useRefreshDomain()
  const updateDomain = useUpdateDomain()

  const confirmDelete = async () => {
    if (deleteId !== null) {
      await new Promise<void>((resolve, reject) => {
        deleteDomain.mutate(deleteId, {
          onSuccess: () => {
            setDeleteId(null)
            resolve()
          },
          onError: (err) => reject(err),
        })
      })
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Domains</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            DNS records, Nginx configs, and SSL certificates
          </p>
        </div>
        <Button type="button" variant="primary" size="sm" className="whitespace-nowrap" onClick={() => setShowModal(true)}>
          Add Domain
        </Button>
      </div>

      {isLoading && (
        <Card className="p-8 animate-pulse">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 bg-muted rounded" />
            ))}
          </div>
        </Card>
      )}

      {!isLoading && domains && (
        <Card className="overflow-hidden">
          {/* Mobile: stacked card layout. Tapping FQDN opens the site; the
              action buttons stay pinned below so they're never scrolled off
              the right edge like a cramped table would do. */}
          <div className="sm:hidden divide-y divide-border">
            {domains.map((d: DomainRecord) => (
              <div key={d.id} className="px-4 py-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <a
                    href={`https://${d.fqdn}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline font-medium text-sm break-all min-w-0 flex-1"
                  >
                    {d.fqdn}
                  </a>
                  <Badge
                    variant={certVariant[d.cert_status] ?? 'muted'}
                    className="gap-1 text-[11px] shrink-0"
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${certDot[d.cert_status] ?? 'bg-muted-foreground'}`}
                    />
                    {d.cert_status}
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                  <span>
                    {d.service_name || (d.service_id ? `#${d.service_id}` : 'no service')}
                  </span>
                  {d.cert_expiry && (
                    <>
                      <span className="opacity-40">·</span>
                      <span>exp {new Date(d.cert_expiry * 1000).toLocaleDateString()}</span>
                    </>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  <RedirectCell
                    domain={d}
                    onSave={(value) =>
                      updateDomain.mutate({ id: d.id, data: { redirect_to: value } })
                    }
                  />
                </div>
                <div className="text-[11px] text-muted-foreground">
                  <AuthCell
                    domain={d}
                    onSave={(value) =>
                      updateDomain.mutate({ id: d.id, data: { auth_token: value } })
                    }
                  />
                </div>
                <div className="flex gap-4 pt-1 border-t border-border/40">
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={() => refreshDomain.mutate(d.id)}
                    disabled={refreshDomain.isPending}
                    loading={refreshDomain.isPending && refreshDomain.variables === d.id}
                    className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground no-underline"
                    title="Force cert state re-check"
                  >
                    {refreshDomain.isPending && refreshDomain.variables === d.id
                      ? 'Refreshing...'
                      : 'Refresh'}
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={() => setConfigId(d.id)}
                    className="h-auto p-0 text-xs text-primary"
                  >
                    Config
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={() => setDeleteId(d.id)}
                    className="h-auto p-0 text-xs text-destructive hover:text-destructive/80"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
            {domains.length === 0 && (
              <div className="px-4 py-8 text-center text-muted-foreground text-sm">
                No domains registered yet.
              </div>
            )}
          </div>
          <div className="hidden sm:block overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.Row className="hover:bg-transparent">
                <Table.Header className="px-4">FQDN</Table.Header>
                <Table.Header className="px-4">Service</Table.Header>
                <Table.Header className="px-4">Redirect To</Table.Header>
                <Table.Header className="px-4">Auth</Table.Header>
                <Table.Header className="px-4">Cert Status</Table.Header>
                <Table.Header className="px-4">Cert Expiry</Table.Header>
                <Table.Header className="px-4">Actions</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {domains.map((d: DomainRecord) => (
                <Table.Row key={d.id}>
                  <Table.Cell className="px-4 py-3 font-medium">
                    <a href={`https://${d.fqdn}`} target="_blank" rel="noreferrer"
                      className="text-primary hover:underline">{d.fqdn}</a>
                  </Table.Cell>
                  <Table.Cell className="px-4 py-3 text-muted-foreground">
                    {d.service_name || (d.service_id ? `#${d.service_id}` : '-')}
                  </Table.Cell>
                  <Table.Cell className="px-4 py-3 text-xs text-muted-foreground">
                    <RedirectCell domain={d} onSave={(value) => updateDomain.mutate({ id: d.id, data: { redirect_to: value } })} />
                  </Table.Cell>
                  <Table.Cell className="px-4 py-3 text-xs text-muted-foreground">
                    <AuthCell domain={d} onSave={(value) => updateDomain.mutate({ id: d.id, data: { auth_token: value } })} />
                  </Table.Cell>
                  <Table.Cell className="px-4 py-3">
                    <Badge variant={certVariant[d.cert_status] ?? 'muted'} className="gap-1 text-[11px]">
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${certDot[d.cert_status] ?? 'bg-muted-foreground'}`}
                      />
                      {d.cert_status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="px-4 py-3 text-muted-foreground text-xs">
                    {d.cert_expiry
                      ? new Date(d.cert_expiry * 1000).toLocaleDateString()
                      : '-'}
                  </Table.Cell>
                  <Table.Cell className="px-4 py-3">
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={() => refreshDomain.mutate(d.id)}
                        disabled={refreshDomain.isPending}
                        loading={refreshDomain.isPending && refreshDomain.variables === d.id}
                        className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground no-underline"
                        title="Force cert state re-check (skip 30s polling wait)"
                      >
                        {refreshDomain.isPending && refreshDomain.variables === d.id ? 'Refreshing...' : 'Refresh'}
                      </Button>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={() => setConfigId(d.id)}
                        className="h-auto p-0 text-xs text-primary"
                      >
                        Config
                      </Button>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={() => setDeleteId(d.id)}
                        className="h-auto p-0 text-xs text-destructive hover:text-destructive/80"
                      >
                        Delete
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
              {domains.length === 0 && (
                <Table.Row className="hover:bg-transparent">
                  <Table.Cell colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    No domains registered yet.
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
          </div>
        </Card>
      )}

      {showModal && <AddDomainModal onClose={() => setShowModal(false)} />}

      <ConfirmDialog
        open={deleteId !== null}
        onOpenChange={(open) => { if (!open) setDeleteId(null) }}
        title="Delete Domain?"
        description="This will remove the DNS record and Nginx config. This cannot be undone."
        confirmLabel={deleteDomain.isPending ? 'Deleting...' : 'Delete'}
        confirmVariant="destructive"
        onConfirm={confirmDelete}
        loading={deleteDomain.isPending}
      />

      {configId !== null && (
        <NginxConfigModal domainId={configId} onClose={() => setConfigId(null)} />
      )}
    </div>
  )
}

function NginxConfigModal({ domainId, onClose }: { domainId: number; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['domain-config', domainId],
    queryFn: () => api.getDomainConfig(domainId),
  })
  const { data: services } = useServices()
  const [config, setConfig] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [result, setResult] = useState('')
  const [selContainer, setSelContainer] = useState('')
  const [selPort, setSelPort] = useState('8080')
  const qc = useQueryClient()

  if (data && !loaded) {
    setConfig(data.config)
    setLoaded(true)
  }

  const generate = useMutation({
    mutationFn: () => {
      const params = new URLSearchParams()
      if (selContainer) { params.set('container', selContainer); params.set('port', selPort) }
      return api.generateDomainConfig(domainId, params.toString())
    },
    onSuccess: (res) => {
      setConfig(res.config)
      setResult(`Generated: ${res.container} → ${res.upstream}`)
    },
    onError: (err: Error) => setResult('Generate failed: ' + err.message),
  })

  const save = useMutation({
    mutationFn: () => api.putDomainConfig(domainId, config),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['domains'] })
      if (res.applied === 'true') {
        setResult('Saved and applied to nginx')
      } else {
        setResult(`Saved but not applied: ${res.reason || 'unknown'}`)
      }
    },
    onError: (err: Error) => setResult('Error: ' + err.message),
  })

  const containerOptions = (services ?? []).map((s: Service) => ({
    value: s.name,
    label: `${s.name} (:${s.container_port})`,
  }))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-3xl mx-4 max-h-[80vh] flex flex-col shadow-lg">
        <div className="p-6 flex flex-col flex-1 min-h-0">
          <h2 className="text-lg font-bold text-card-foreground mb-1">
            Nginx Config — {data?.fqdn || '...'}
          </h2>
          <p className="text-xs text-muted-foreground mb-2">
            Select a container to proxy, or edit the config manually.
          </p>
          {/* Proxy target picker — container / port / Generate. Each field
              labeled with a FormRow so operators can tell what's what at
              a glance. Stacked on mobile, inline on sm+. */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-end mb-3">
            <FormRow label="Container" stacked className="min-w-0">
              <Select
                value={selContainer}
                onValueChange={(v) => {
                  setSelContainer(v)
                  const svc = services?.find(s => s.name === v)
                  if (svc) setSelPort(String(svc.container_port))
                }}
                options={containerOptions}
                placeholder="Select container..."
              />
            </FormRow>
            <FormRow label="Port" stacked className="sm:w-24">
              <Input
                type="number"
                placeholder="8080"
                value={selPort}
                onChange={e => setSelPort(e.target.value)}
              />
            </FormRow>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => generate.mutate()}
              disabled={generate.isPending || !selContainer}
              loading={generate.isPending}
              className="bg-success text-success-foreground hover:bg-success/90 shrink-0 sm:mb-0"
            >
              {generate.isPending ? '...' : 'Generate'}
            </Button>
          </div>

          {isLoading && <div className="animate-pulse h-60 bg-muted rounded" />}

          {loaded && (
            <textarea
              className="flex-1 w-full rounded-md border border-border bg-card px-3 py-2 text-xs font-mono text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring min-h-[300px]"
              value={config}
              onChange={e => setConfig(e.target.value)}
              spellCheck={false}
            />
          )}

          {result && (
            <div
              className={
                'mt-2 text-xs px-3 py-2 rounded border ' +
                (result.includes('applied')
                  ? 'bg-success-soft text-success-soft-foreground border-success-soft'
                  : 'bg-warning-soft text-warning-soft-foreground border-warning-soft')
              }
            >
              {result}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-3">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => save.mutate()}
              disabled={save.isPending}
              loading={save.isPending}
            >
              {save.isPending ? 'Saving...' : 'Save & Apply'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

function AddDomainModal({ onClose }: { onClose: () => void }) {
  const { data: services } = useServices()
  const create = useCreateDomain()
  const [form, setForm] = useState({
    fqdn: '',
    service_id: 0,
    ttl: 300,
    redirect_to: '',
    auth_token: '',
  })

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    create.mutate(
      {
        fqdn: form.fqdn,
        service_id: form.service_id || undefined,
        ttl: form.ttl,
        redirect_to: form.redirect_to.trim() || undefined,
        auth_token: form.auth_token.trim() || undefined,
      },
      { onSuccess: onClose },
    )
  }

  const serviceOptions = [
    { value: '0', label: 'None' },
    ...(services ?? []).map((s: Service) => ({
      value: String(s.id),
      label: s.name,
    })),
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md mx-4 shadow-lg">
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <h2 className="text-lg font-bold text-card-foreground">Add Domain</h2>

          <FormRow label="FQDN" required stacked>
            <Input
              required
              placeholder="app.pentarab.com"
              value={form.fqdn}
              onChange={(e) => setForm({ ...form, fqdn: e.target.value })}
            />
          </FormRow>

          <FormRow label="Service" stacked>
            <Select
              value={String(form.service_id)}
              onValueChange={(v) => setForm({ ...form, service_id: Number(v) })}
              options={serviceOptions}
            />
          </FormRow>

          <FormRow label="Redirect To" help="Optional — 301 instead of proxy" stacked>
            <Input
              placeholder="www.issuebbal.com"
              value={form.redirect_to}
              onChange={(e) => setForm({ ...form, redirect_to: e.target.value })}
            />
          </FormRow>

          <FormRow label="Auth Token" help="Optional — require Authorization: Bearer <token>. Empty = public." stacked>
            <div className="flex items-center gap-2">
              <Input
                className="font-mono text-xs"
                placeholder="empty = public"
                value={form.auth_token}
                onChange={(e) => setForm({ ...form, auth_token: e.target.value })}
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setForm({ ...form, auth_token: genAuthToken() })}
              >
                Generate
              </Button>
            </div>
          </FormRow>

          <FormRow label="TTL (seconds)" stacked>
            <Input
              type="number"
              value={form.ttl}
              onChange={(e) => setForm({ ...form, ttl: Number(e.target.value) })}
            />
          </FormRow>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="sm" loading={create.isPending}>
              {create.isPending ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
